/**
 * Atomic internal_notes append via database RPC.
 * Eliminates read-modify-write race conditions (Bug 65).
 * 
 * All callers build the formatted log entry string client-side,
 * then pass it here for atomic server-side concatenation.
 */

import { supabase } from "@/integrations/supabase/client";

/**
 * Atomically appends a note to an order's internal_notes field.
 * Uses a SECURITY DEFINER function that checks user_id = auth.uid().
 * 
 * @param orderId - The order UUID
 * @param noteText - The pre-formatted log entry string
 * @param setWorkflow - Whether to also update last_workflow_action_at (default true)
 * @returns The full updated internal_notes string
 */
export async function appendNoteAtomic(
  orderId: string,
  noteText: string,
  setWorkflow: boolean = true,
): Promise<string> {
  const { data, error } = await supabase.rpc("append_internal_note", {
    p_order_id: orderId,
    p_note: noteText,
    p_set_workflow: setWorkflow,
  });

  if (error) throw error;
  return data as string;
}

/**
 * Appends multiple note entries atomically (joined with newlines).
 * Useful when a single action produces multiple log lines (e.g. payment + status change).
 */
export async function appendNotesAtomic(
  orderId: string,
  noteTexts: string[],
  setWorkflow: boolean = true,
): Promise<string> {
  const combined = noteTexts.join("\n");
  return appendNoteAtomic(orderId, combined, setWorkflow);
}
